import { useEffect, useMemo, useState } from 'react';
import { getEventSupportContact, listEvents, listMyConnectActiveEvents } from '../api/services/EventsService';
import {
  ACTIVE_EVENT_SELECTION_CHANGED,
  readSelectedActiveEventId,
} from '../../features/events/active-events/active-event-selection';
import { buildSupportContact, type SupportContact } from './supportLinks';

type EventSupportFields = { supportPhone?: string | null; supportEmail?: string | null };

const CACHE_TTL_MS = 60_000;
const fieldsCache = new Map<string, { at: number; promise: Promise<EventSupportFields | null> }>();
let eventsCache: { at: number; promise: Promise<any[]> } | null = null;

/** Testler arasında önbelleği temizler. */
export const resetEventSupportContactCache = () => {
  fieldsCache.clear();
  eventsCache = null;
};

const unwrap = (payload: any) => payload?.data?.data ?? payload?.data ?? payload ?? null;

const extractList = (payload: any): any[] => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

/** Ana sayfadaki "kayıtlı tercih": B2B seçimi, yoksa kayıtlı aktif etkinlik. */
const readStoredPreferredEventId = (): string => {
  try {
    const b2b = window.localStorage.getItem('b2bActiveEventId');
    if (b2b) return b2b.trim();
    const raw = window.localStorage.getItem('activeEvent');
    const parsed = raw ? JSON.parse(raw) : null;
    return String(parsed?.activeEventId || parsed?.eventId || '').trim();
  } catch {
    return '';
  }
};

const endTimestamp = (event: any) => {
  const end = new Date(event?.endDate || event?.startDate || '').getTime();
  return Number.isNaN(end) ? Number.NaN : end;
};

const isPast = (event: any) => {
  const end = endTimestamp(event);
  return !Number.isNaN(end) && end < Date.now();
};

/** Ana sayfadaki sıralama: önce connectPriority, sonra bitiş tarihi. */
const sortByPriority = (items: any[]) =>
  items.slice().sort((a, b) => {
    const left = Number(a?.connectPriority) > 0 ? Number(a.connectPriority) : 1;
    const right = Number(b?.connectPriority) > 0 ? Number(b.connectPriority) : 1;
    if (left !== right) return left - right;
    const l = endTimestamp(a);
    const r = endTimestamp(b);
    if (Number.isNaN(l) && Number.isNaN(r)) return 0;
    if (Number.isNaN(l)) return 1;
    if (Number.isNaN(r)) return -1;
    return l - r;
  });

/**
 * Ana sayfa ile BİREBİR aynı etkinlik seçimi (böylece panel, kullanıcının gördüğü etkinliğin bilgisini gösterir):
 * - "Connect aktif" etkinlik varsa ilk (öncelikli) aktif etkinlik; kayıtlı eski seçim BUNU ezmez.
 * - Birden fazla B2B/Connect aktif etkinlik varsa ve kullanıcı listeden birini seçtiyse seçtiği.
 * - Hiç aktif etkinlik yoksa kayıtlı tercih, o da yoksa tarihi geçmemiş yayındaki ilk etkinlik.
 */
export const pickSupportEventId = (events: any[], sharedSelectedId: string, storedPreferredId: string): string => {
  const upcoming = events.filter((event) => !isPast(event));
  const connectActive = upcoming.filter((event) => event?.eventIsActive === true);
  const primary = sortByPriority(connectActive.length > 0 ? connectActive : upcoming)[0];
  const allActive = sortByPriority(
    upcoming.filter((event) => event?.eventIsActive === true || event?.b2bIsActive === true),
  );

  if (sharedSelectedId && allActive.length >= 2 && allActive.some((event) => String(event?.id) === sharedSelectedId)) {
    return sharedSelectedId;
  }
  if (connectActive.length === 0 && storedPreferredId) {
    const stored = upcoming.find((event) => String(event?.id) === storedPreferredId);
    if (stored) return storedPreferredId;
  }
  return primary?.id ? String(primary.id) : '';
};

/** Header ve Destek paneli aynı anda çağırdığında tek istek atılır. */
const loadPublishedEvents = (): Promise<any[]> => {
  if (eventsCache && Date.now() - eventsCache.at < CACHE_TTL_MS) return eventsCache.promise;
  const promise = Promise.resolve()
    .then(() => listEvents({ status: 'published', public: true }))
    .then(extractList)
    .catch(() => {
      eventsCache = null;
      return [] as any[];
    });
  eventsCache = { at: Date.now(), promise };
  return promise;
};

const resolveEventId = async (sharedSelectedId: string): Promise<string> => {
  const fromPublished = pickSupportEventId(await loadPublishedEvents(), sharedSelectedId, readStoredPreferredEventId());
  if (fromPublished) return fromPublished;
  // Yayındaki etkinlik listesi okunamadı/boş: kayıtlı seçime, o da yoksa kullanıcının ilk aktif etkinliğine düş.
  const stored = sharedSelectedId || readStoredPreferredEventId();
  if (stored) return stored;
  try {
    return String(extractList(await listMyConnectActiveEvents())[0]?.id || '');
  } catch {
    return '';
  }
};

const loadSupportFields = (eventId: string): Promise<EventSupportFields | null> => {
  const hit = fieldsCache.get(eventId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
  const promise = Promise.resolve()
    .then(() => getEventSupportContact(eventId))
    .then((res) => unwrap(res) as EventSupportFields)
    .catch(() => {
      fieldsCache.delete(eventId);
      return null;
    });
  fieldsCache.set(eventId, { at: Date.now(), promise });
  return promise;
};

/**
 * Kullanıcının gördüğü (seçili) etkinliğin admin ayarlarında tanımlı destek hattı (WhatsApp + e-posta).
 * Etkinlikte tanımlı değilse veya yüklenemezse ortam değişkenindeki varsayılanlar kullanılır.
 * `enabled=false` (ör. herkese açık rozet sayfası): hiç istek atılmaz, varsayılanlar döner.
 */
export function useEventSupportContact(enabled = true): SupportContact {
  const [sharedId, setSharedId] = useState(readSelectedActiveEventId);
  const [fields, setFields] = useState<EventSupportFields | null>(null);

  useEffect(() => {
    const sync = () => setSharedId(readSelectedActiveEventId());
    window.addEventListener(ACTIVE_EVENT_SELECTION_CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(ACTIVE_EVENT_SELECTION_CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setFields(null);
    if (!enabled) return undefined;
    (async () => {
      const id = await resolveEventId(sharedId);
      if (!id) return;
      const result = await loadSupportFields(id);
      if (mounted) setFields(result);
    })();
    return () => {
      mounted = false;
    };
  }, [sharedId, enabled]);

  return useMemo(
    () => buildSupportContact(enabled ? { phone: fields?.supportPhone, email: fields?.supportEmail } : null),
    [fields, enabled],
  );
}
